import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
    onAuthStateChanged,
    signOut,
    GoogleAuthProvider,
    signInWithPopup,
    signInWithPhoneNumber,
    RecaptchaVerifier
} from 'firebase/auth';
import { auth } from '../firebase';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [userProfile, setUserProfile] = useState({ code: 1 }); // 1: Initial/Loading, 2: Logged In, 3: Logged Out
    const [idToken, setIdToken] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoginLoading, setIsLoginLoading] = useState(false);
    const [mobileNumber, setMobileNumber] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [otp, setOtp] = useState("");
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [recaptchaContainerId, setRecaptchaContainerId] = useState(`recaptcha-container-${Date.now()}`);
    const [onLoginSuccess, setOnLoginSuccess] = useState(null);

    const recaptchaVerifierRef = useRef(null);

    // Monitor auth state
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const token = await user.getIdToken(true);
                setIdToken(token);
                setUserProfile({
                    code: 2,
                    displayName: user.displayName || user.phoneNumber,
                    photoURL: user.photoURL || '/profileDefault.png',
                    email: user.email,
                    phoneNumber: user.phoneNumber
                });

                // Set up token refresh
                const interval = setInterval(async () => {
                    const refreshedToken = await user.getIdToken(true);
                    setIdToken(refreshedToken);
                }, 50 * 60 * 1000);
                return () => clearInterval(interval);
            } else {
                setIdToken(null);
                setUserProfile({ code: 3 });
            }
        });

        return () => unsubscribe();
    }, []);

    const openLoginModal = (callback) => {
        if (typeof callback === 'function') {
            setOnLoginSuccess(() => callback);
        }
        setIsModalOpen(true);
    };
    const closeLoginModal = () => {
        setIsModalOpen(false);
        setMobileNumber("");
        setOtp("");
        setOtpSent(false);
        setConfirmationResult(null);
        setOnLoginSuccess(null);
        if (recaptchaVerifierRef.current) {
            try {
                recaptchaVerifierRef.current.clear();
            } catch (e) {
            }
            recaptchaVerifierRef.current = null;
        }
    };

    const handleSendOtp = async (phoneNumber) => {
        if (isLoginLoading) return;

        try {
            setIsLoginLoading(true);

            if (recaptchaVerifierRef.current) {
                try {
                    recaptchaVerifierRef.current.clear();
                } catch (e) {
                }
                recaptchaVerifierRef.current = null;
            }

            const newId = `recaptcha-container-${Date.now()}`;
            setRecaptchaContainerId(newId);

            // Give DOM time to update with new container ID
            await new Promise(resolve => setTimeout(resolve, 100));

            recaptchaVerifierRef.current = new RecaptchaVerifier(auth, newId, {
                size: "invisible",
                callback: () => {
                },
            });

            const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifierRef.current);
            setConfirmationResult(confirmation);
            setOtpSent(true);
            return { success: true };
        } catch (error) {
            console.error("Error sending OTP:", error);
            return { success: false, error };
        } finally {
            setIsLoginLoading(false);
        }
    };

    const handleVerifyOtp = async (otpCode) => {
        if (isLoginLoading || !confirmationResult) return;

        try {
            setIsLoginLoading(true);
            const result = await confirmationResult.confirm(otpCode);
            const user = result.user;
            const token = await user.getIdToken(true);

            setIdToken(token);
            setUserProfile({
                code: 2,
                displayName: user.displayName || user.phoneNumber,
                photoURL: user.photoURL || './profileDefault.png',
                email: user.email,
                phoneNumber: user.phoneNumber
            });

            closeLoginModal();
            if (onLoginSuccess) {
                onLoginSuccess();
            }
            return { success: true };
        } catch (error) {
            console.error("Error verifying OTP:", error);
            return { success: false, error };
        } finally {
            setIsLoginLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        if (isLoginLoading) return;

        try {
            setIsLoginLoading(true);
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);
            const user = result.user;
            const token = await user.getIdToken(true);

            setIdToken(token);
            setUserProfile({
                code: 2,
                displayName: user.displayName || user.phoneNumber,
                photoURL: user.photoURL || './profileDefault.png',
                email: user.email,
                phoneNumber: user.phoneNumber
            });

            closeLoginModal();
            if (onLoginSuccess) {
                onLoginSuccess();
            }
            return { success: true };
        } catch (error) {
            console.error("Error with Google login:", error);
            return { success: false, error };
        } finally {
            setIsLoginLoading(false);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            setUserProfile({ code: 3 });
            setIdToken(null);
            return { success: true };
        } catch (error) {
            console.error("Logout failed:", error);
            return { success: false, error };
        }
    };

    const value = {
        userProfile,
        idToken,
        isModalOpen,
        isLoginLoading,
        mobileNumber,
        setMobileNumber,
        otpSent,
        otp,
        setOtp,
        recaptchaContainerId,
        openLoginModal,
        closeLoginModal,
        handleSendOtp,
        handleVerifyOtp,
        handleGoogleLogin,
        handleLogout
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
